// KPI status helper — shared by the table dot ("Ocena"), the yearly bar colors,
// and the colored metric name. Status is computed automatically from realized vs target
// (spec: ispod targeta = crveno, na targetu = žuto, iznad targeta = zeleno).
//
// NOTE: the agent screenshot also shows an ORANGE dot. The exact rule that produces it
// is an open question for the BE team (see docs/kpi-api-proposal.md). We expose it here so
// the UI can render a server-provided status verbatim once the backend returns one.

export type KpiStatus = "below" | "on" | "above" | "warning";

export const KPI_STATUS_COLORS: Record<KpiStatus, string> = {
  below: "#D75060", // Red/Primary — ispod targeta
  on: "#EDDF4A", // Yellow/Primary — na targetu
  above: "#60B046", // Green/Primary — iznad targeta
  warning: "#DD992D", // Orange/Primary — server-provided edge state
};

// Text colors from the design spec.
export const KPI_TEXT_GRAY = "#636A79";
export const KPI_TEXT_DARK = "#323343";

// Pie / donut palette.
export const KPI_QUALIFICATION_COLORS = {
  agent: "#B046AC", // Plum/Primary
  web: "#DF86DC", // Plum/Secondary
  app: "#FFC6FD", // Plum/Light
} as const;

export const KPI_BANK_COLORS = {
  bankNow: "#60B046", // green
  cembra: "#3B82C4", // blue
  corner: "#D75060", // red
} as const;

// Disqualification reasons reuse the status palette for visual variety.
export const KPI_REASON_COLORS = {
  clientCanceled: "#DD992D",
  rejectedWk: "#D75060",
  rejectedBank: "#B046AC",
} as const;

// Progress bar / ring colors.
export const KPI_PROGRESS_RED = "#D75060";
export const KPI_PROGRESS_TRACK = "#F1F2F4";
export const KPI_RING_GRADIENT_FROM = "#EDDF4A";
export const KPI_RING_GRADIENT_TO = "#D4B800";

// Alternating table row backgrounds.
export const KPI_ROW_BG_ODD = "#F1F2F4"; // Gray sky / 1
export const KPI_ROW_BG_EVEN = "#E0E2E6"; // Gray sky / 2

export const getKpiStatus = (value: number, target: number): KpiStatus => {
  if (target <= 0) {
    return "on";
  }

  if (value < target) {
    return "below";
  }

  if (value === target) {
    return "on";
  }

  return "above";
};

export const getKpiStatusColor = (value: number, target: number): string =>
  KPI_STATUS_COLORS[getKpiStatus(value, target)];

// Prefer a backend-provided status string; fall back to computing it from value/target.
export const resolveStatusColor = (
  status: string | null | undefined,
  value: number,
  target: number,
): string => {
  if (status && status in KPI_STATUS_COLORS) {
    return KPI_STATUS_COLORS[status as KpiStatus];
  }
  return getKpiStatusColor(value, target);
};

// % ostvarenja — clamped only for display purposes is left to the caller.
export const getAchievementPercent = (value: number, target: number): number => {
  if (target <= 0) {
    return 0;
  }

  return Math.round((value / target) * 100);
};
